/**
 * Insight Telemetry — quiet operational observation
 * ===================================================
 *
 * What this is:
 *   A tiny, in-memory buffer that lets us learn what operators actually
 *   open, which chip paths they follow, and where they abandon. The whole
 *   point is to reduce confusion and improve Help Mode usefulness —
 *   nothing more.
 *
 * What this is NOT:
 *   - Marketing analytics
 *   - Engagement tracking
 *   - Funnels / retention / growth charts
 *   - Behavioural surveillance
 *
 * Operator-facing footprint: zero. No UI, no toasts, no badges.
 * Buffer: in-memory only, flushed in batches of 10 or on mode close, plus
 *         a `sendBeacon` on tab unload. Failures are silent.
 * Disable: localStorage["insight.telemetry.off"] = "1" turns the whole
 *          thing into a no-op for that browser, instantly.
 */
import api from "../lib/api";

const ENDPOINT = "/operational-base/insight/telemetry";
const FLUSH_THRESHOLD = 10;
const MAX_BUFFER = 50;

let buffer = [];
let sessionId = null;
let openedAt = null;
let depth = 0;
let lastEntryKey = null;

const isDisabled = () => {
    try {
        return localStorage.getItem("insight.telemetry.off") === "1";
    } catch {
        return false;
    }
};

const newSessionId = () => {
    try {
        if (window.crypto && typeof window.crypto.randomUUID === "function") {
            return window.crypto.randomUUID();
        }
    } catch {
        /* fall through */
    }
    return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const bucketViewport = () => {
    try {
        const w = window.innerWidth || 0;
        if (w < 768) return "mobile";
        if (w < 1024) return "tablet";
        return "desktop";
    } catch {
        return null;
    }
};

const currentRoute = () => {
    try {
        return window.location?.pathname || null;
    } catch {
        return null;
    }
};

const enqueue = (event) => {
    if (isDisabled()) return;
    if (!sessionId) sessionId = newSessionId();
    buffer.push({
        ...event,
        session_id: sessionId,
        route: event.route || currentRoute(),
        viewport: event.viewport || bucketViewport(),
        client_ts: new Date().toISOString(),
    });
    if (buffer.length >= FLUSH_THRESHOLD) flush();
    if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
};

const flush = async () => {
    if (isDisabled() || buffer.length === 0) return;
    const batch = buffer.splice(0, buffer.length);
    try {
        await api.post(ENDPOINT, { events: batch });
    } catch {
        // Calm failure — drop the batch silently. Telemetry must never
        // surface as an operator concern.
    }
};

/**
 * Best-effort flush during tab unload. Falls back to fetch with keepalive
 * if sendBeacon is unavailable.
 */
const flushOnUnload = () => {
    if (isDisabled() || buffer.length === 0) return;
    const batch = buffer.splice(0, buffer.length);
    try {
        // Runtime origin — see lib/api.js. Avoids CRA baking a stale preview
        // URL into the production bundle (would break sendBeacon w/ CORS).
        const base = typeof window !== "undefined" ? window.location.origin : "";
        const url = `${base}/api${ENDPOINT}`;
        const token = (() => {
            try { return localStorage.getItem("token"); } catch { return null; }
        })();
        // sendBeacon doesn't support custom headers, so we attach the token
        // inline. The backend `get_current_user` accepts the Authorization
        // header preferentially; for unload-time flushes we accept that some
        // events may not authenticate and be dropped server-side. This is a
        // deliberate trade-off — we never want unload to block the operator.
        const payload = new Blob(
            [JSON.stringify({ events: batch, _token: token || null })],
            { type: "application/json" }
        );
        if (navigator.sendBeacon) {
            navigator.sendBeacon(url, payload);
        } else if (typeof fetch === "function") {
            fetch(url, {
                method: "POST",
                keepalive: true,
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ events: batch }),
            }).catch(() => { /* silent */ });
        }
    } catch {
        /* silent */
    }
};

// Register unload listener once.
if (typeof window !== "undefined" && !window.__insightTelemetryUnloadBound) {
    window.__insightTelemetryUnloadBound = true;
    window.addEventListener("pagehide", flushOnUnload);
    window.addEventListener("beforeunload", flushOnUnload);
}

// ----------------------------------------------------------------------
// Public surface — exactly four observation signals
// ----------------------------------------------------------------------

export const trackModeOpened = () => {
    sessionId = newSessionId();
    openedAt = Date.now();
    depth = 0;
    lastEntryKey = null;
    enqueue({ action: "mode_opened" });
};

export const trackEntryOpened = (insightKey) => {
    if (!insightKey) return;
    depth += 1;
    lastEntryKey = insightKey;
    enqueue({ action: "entry_opened", insight_key: insightKey });
};

export const trackChipTraversed = (fromKey, toKey) => {
    if (!toKey) return;
    enqueue({
        action: "chip_traversed",
        insight_key: toKey,
        from_key: fromKey || lastEntryKey || null,
    });
};

export const trackModeClosed = () => {
    if (!openedAt) {
        // Nothing to flush — mode was never opened in this session.
        return;
    }
    const duration = Date.now() - openedAt;
    enqueue({
        action: "mode_closed",
        duration_ms: duration,
        depth,
    });
    openedAt = null;
    // Flush immediately on close — the most operationally meaningful moment.
    flush();
};

/** Manual flush — used by tests / debugging. */
export const flushTelemetry = flush;
